import { useEffect, useState } from 'react';
import { LOCALE_NATIVE_NAMES, SUPPORTED_LOCALES, type Locale, type Permission } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  Boxes,
  ClipboardCheck,
  ClipboardList,
  DatabaseBackup,
  FolderTree,
  LayoutDashboard,
  Map,
  Menu,
  Package,
  ScrollText,
  Search,
  Settings,
  Truck,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react';
import { changeLocale } from '../lib/i18n';
import { apiFetch } from '../lib/api';
import { useInvalidateSession, useSession } from '../lib/session';
import { useIsBelowLaptop } from '../lib/use-media-query';
import { clearWarehouseSelection } from '../lib/warehouse';
import { WarehouseSwitcher } from './warehouses/warehouse-switcher';
import { BarcodeScannerListener } from './product/barcode-scanner';
import { NotificationBell } from './alerts/notification-bell';
import { CommandPalette } from './command-palette/command-palette';
import { useGlobalShortcuts } from './command-palette/use-global-shortcuts';
import './app-shell.css';

/**
 * 应用外壳（UX 设计书第一部分 §3 + 第四部分 §1 响应式策略）。
 *
 * 【响应式，阶段 2–4】四档断点（docs/02-ux-ia.md）：
 *   <768px    手机：顶栏收敛（语言/用户/登出进抽屉），汉堡抽屉导航
 *   768–1023  平板：同上（汉堡抽屉——现场主力设备）
 *   1024–1439 笔记本：侧栏收成 Lucide 图标条（此前是 72px 纯文字截断条）
 *   ≥1440     桌面：完整侧栏（图标+文字），现状不变
 * 导航按 `permission` 过滤仅是降噪，不是安全边界（后端 Guard 才是，Spec §43）。
 */
export interface NavItem {
  readonly to: string;
  readonly labelKey: string;
  readonly permission?: Permission;
  readonly icon: LucideIcon;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', permission: 'warehouse.view', icon: LayoutDashboard },
  { to: '/inventory', labelKey: 'nav.inventory', permission: 'inventory.view', icon: Boxes },
  { to: '/search', labelKey: 'nav.search', permission: 'inventory.view', icon: Search },
  { to: '/layout', labelKey: 'nav.layout', permission: 'warehouse.view', icon: Map },
  { to: '/locations', labelKey: 'nav.locations', permission: 'warehouse.view', icon: FolderTree },
  { to: '/movements', labelKey: 'nav.movements', permission: 'inventory.view', icon: ArrowLeftRight },
  { to: '/alerts', labelKey: 'nav.alerts', permission: 'warehouse.view', icon: AlertTriangle },
  // M7：读路径 = inventory.view（决策 E）；写操作在页面内部各自用 inventory.reserve 把关（决策 H）。
  { to: '/orders', labelKey: 'nav.orders', permission: 'inventory.view', icon: ClipboardList },
  // M9：读路径 = warehouse.view，创建/审批在页面内用 stockcount.* 把关（D-29 第 6 条）。
  { to: '/stock-counts', labelKey: 'nav.stockCount', permission: 'warehouse.view', icon: ClipboardCheck },
  { to: '/products', labelKey: 'nav.products', permission: 'inventory.view', icon: Package },
  { to: '/suppliers', labelKey: 'nav.suppliers', permission: 'supplier.view', icon: Truck },
  // M10：导入向导要求 import.execute（S17，§33）；导出中心复用既有 export.data。
  { to: '/import-jobs', labelKey: 'nav.importCenter', permission: 'import.execute', icon: Upload },
  { to: '/exports', labelKey: 'nav.reports', permission: 'export.data', icon: BarChart3 },
  // 【审计双版本】导航挂弱码（运营版）：管理人员持运营版可进；ADMIN 持全部
  // 权限自然可进；服务端按持有的码决定是否过滤系统级事件
  { to: '/audit-log', labelKey: 'nav.auditLog', permission: 'audit.view.operational', icon: ScrollText },
  // M11：backup.view 是 ADMIN-only（未加入 MANAGER_PERMISSIONS，见 permissions.ts）
  { to: '/backup-status', labelKey: 'nav.backupStatus', permission: 'backup.view', icon: DatabaseBackup },
];

export function AppShell(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { data: session } = useSession();
  const invalidateSession = useInvalidateSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const belowLaptop = useIsBelowLaptop();
  const [navOpen, setNavOpen] = useState(false);

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.permission || (session?.permissions.includes(item.permission) ?? false),
  );

  useGlobalShortcuts(() => setPaletteOpen(true));

  // 路由变化即收起抽屉（点击导航项后不该留着遮罩）；回到 ≥1024px 也收起
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, belowLaptop]);

  // 抽屉打开时锁滚动 + Esc 关闭
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  const logout = async (): Promise<void> => {
    await apiFetch('/auth/logout', { method: 'POST' });
    clearWarehouseSelection();
    await invalidateSession();
    navigate('/login', { replace: true });
  };

  const localeButtons = (
    <nav className="shell__locales" aria-label={t('shell.language')}>
      {SUPPORTED_LOCALES.map((locale: Locale) => (
        <button
          key={locale}
          type="button"
          className="shell__locale"
          aria-current={i18n.language === locale}
          onClick={() => void changeLocale(locale)}
        >
          {LOCALE_NATIVE_NAMES[locale]}
        </button>
      ))}
    </nav>
  );

  // 点击导航项一律收抽屉——不能只依赖路由变化的 effect：点"当前页"的链接时
  // pathname 不变，effect 不触发，抽屉会带着遮罩留在原地（Playwright 抓到的真缺陷）
  const closeNav = (): void => setNavOpen(false);

  const navList = (
    <ul>
      {visibleNavItems.map((item) => (
        <li key={item.to}>
          <NavLink to={item.to} className="shell__nav-link" title={t(item.labelKey)} onClick={closeNav}>
            <item.icon className="shell__nav-icon" aria-hidden />
            <span className="shell__nav-label">{t(item.labelKey)}</span>
          </NavLink>
        </li>
      ))}
      <li>
        <NavLink to="/settings" className="shell__nav-link" title={t('nav.settings')} onClick={closeNav}>
          <Settings className="shell__nav-icon" aria-hidden />
          <span className="shell__nav-label">{t('nav.settings')}</span>
        </NavLink>
      </li>
    </ul>
  );

  return (
    <div className="shell">
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {session?.permissions.includes('inventory.view') && <BarcodeScannerListener />}
      <header className="shell__topbar">
        {belowLaptop && (
          <button
            type="button"
            className="shell__hamburger"
            aria-label={t('shell.openNav')}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            {navOpen ? <X aria-hidden /> : <Menu aria-hidden />}
          </button>
        )}
        <span className="shell__brand">{t('app.name')}</span>

        {/* 仓库上下文必须显而易见（Spec §5） */}
        <WarehouseSwitcher />

        <div className="shell__spacer" />

        {/* <1024px 语言选择收进抽屉（顶栏三个常驻语言按钮占 166px，是手机端
            全站 300px 横向溢出的最大单项来源——审计 R-01/R-05） */}
        {!belowLaptop && localeButtons}

        {session && (
          <div className="shell__user">
            <NotificationBell />
            <NavLink
              to="/settings"
              className="shell__nav-link shell__user-name"
              title={t('shell.signedInAs', { name: session.displayName })}
            >
              {session.displayName}
            </NavLink>
            <button
              type="button"
              className="btn btn--ghost btn--sm shell__logout"
              onClick={() => void logout()}
            >
              {t('shell.logout')}
            </button>
          </div>
        )}
      </header>

      <div className="shell__body">
        {/* ≥1024px：常驻侧栏（1024–1439 图标条 / ≥1440 完整）；<1024px：汉堡抽屉 */}
        {!belowLaptop && <aside className="shell__sidebar">{navList}</aside>}

        {belowLaptop && navOpen && (
          <div
            className="nav-drawer__overlay"
            role="presentation"
            onClick={() => setNavOpen(false)}
          >
            <aside
              className="nav-drawer"
              aria-label={t('shell.openNav')}
              onClick={(e) => e.stopPropagation()}
            >
              {navList}
              <div className="nav-drawer__footer">
                {localeButtons}
                {session && (
                  <button
                    type="button"
                    className="btn btn--ghost shell__logout--drawer"
                    onClick={() => void logout()}
                  >
                    {t('shell.logout')}
                  </button>
                )}
              </div>
            </aside>
          </div>
        )}

        <main className="shell__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
